import {tryExecution} from '../_util';

class Dropzone {
  /**
   * @type {HTMLInputElement}
   * @private
   */
  _el;

  /**
   * @type {HTMLElement|null}
   * @private
   */
  _prompt = null;

  /**
   * @type {HTMLElement|null}
   * @private
   */
  _firstLineElement = null;

  /**
   * @type {HTMLElement|null}
   * @private
   */
  _secondLineElement = null;

  /**
   * @type {HTMLElement|null}
   * @private
   */
  _iconElement = null;

  /**
   * @type {HTMLElement|null}
   * @private
   */
  _closeButtonElement = null;

  /**
   * @type {string|null}
   * @private
   */
  _maxBytes = null;

  /**
   * @type {string[]|null}
   * @private
   */
  _allowedExtensions = null;

  /**
   * @type {boolean}
   * @private
   */
  _allowMultiple = false;

  /**
   * @param {HTMLInputElement} el
   */
  constructor(el) {
    this._el = el;

    if (el.dataset.allowedExtensions != null) {
      this._allowedExtensions = el.dataset.allowedExtensions
        ?.split(',')
        .map(x => String.prototype.trim.call(x))
        .filter(x => !!x);
    }

    if (typeof el.dataset.maxBytes === "string") {
      this._maxBytes = el.dataset.maxBytes;
    }

    this._allowMultiple = el.multiple;

    this.start();
    this.attachEvents();
  }

  start() {
    const [ prompt, firstLine, secondLine, icon, closeButton ] = this.createPrompt();

    this._prompt = prompt;
    this._firstLineElement = firstLine;
    this._secondLineElement = secondLine;
    this._iconElement = icon;
    this._closeButtonElement = closeButton;

    this._el.before(prompt);
    this.file = null;
  }

  attachEvents() {
    this._prompt.addEventListener('click', (event) => {
      const { target } = event;

      if (this.filled === false || target.classList.contains('dropzone__upload')) {
        this._el.click();
      }
    });

    this._el.addEventListener('change', (event) => {
      this.file = event.target.files;
    });

    this._prompt.addEventListener('drop', (event) => {
      event.preventDefault();

      this.file = event.dataTransfer.files;
      this.dragover = false;
    });

    this._prompt.addEventListener('dragover', (event) => {
      event.preventDefault();

      this.dragover = true;
    });

    this._prompt.addEventListener('dragleave', (event) => {
      event.preventDefault();

      this.dragover = false;
    });

    this._closeButtonElement.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();

      this.file = null;
    })
  }

  createPrompt() {
    const prompt = document.createElement('div');
    prompt.classList.add('dropzone__prompt');

    // Copy status-classes to prompt
    for (const promptClass of this._el.classList) {
      if (/^is-/.test(promptClass)) {
        prompt.classList.add(promptClass);
      }
    }

    const contentWrapper = document.createElement('div');
    contentWrapper.classList.add('dropzone__content');

    const textWrapper = document.createElement('div');
    textWrapper.classList.add('dropzone__text');

    const firstLineWrapper = document.createElement('div');
    firstLineWrapper.classList.add('dropzone__text__firstline');

    const secondLineWrapper = document.createElement('div');
    secondLineWrapper.classList.add('dropzone__text__secondline');

    textWrapper.append(firstLineWrapper);
    textWrapper.append(secondLineWrapper);

    const iconWrapper = document.createElement('div');
    iconWrapper.classList.add('dropzone__content__icon');

    const icon = document.createElement('i');
    icon.classList.add('fa', 'fa-upload');

    iconWrapper.append(icon);

    contentWrapper.append(iconWrapper);
    contentWrapper.append(textWrapper);
    prompt.append(contentWrapper);

    const closeWrapper = document.createElement('div');
    closeWrapper.classList.add('dropzone__close');

    const closeButton = document.createElement('button')
    closeButton.type = 'button';
    closeButton.classList.add('dropzone__close__button')

    const closeIcon = document.createElement('i');
    closeIcon.classList.add('fa', 'fa-close');

    closeButton.append(closeIcon);
    closeWrapper.append(closeButton);
    prompt.append(closeWrapper);

    return [ prompt, firstLineWrapper, secondLineWrapper, icon, closeButton ];
  }

  generateFileSpecification() {
    const parts = [];

    if (Array.isArray(this._allowedExtensions) && this._allowedExtensions.length > 0) {
      parts.push(`Formate: ${this._allowedExtensions.map(x => String.prototype.toUpperCase.call(x)).join(', ')}`);
    }

    if (typeof this._maxBytes === "string" && this._maxBytes.length > 0) {
      parts.push(`Größe: ${this._maxBytes}`);
    }

    return parts.join(' · ');
  }

  /**
   * @param {boolean} value
   */
  set dragover(value) {
    if (value) {
      this._prompt.classList.add('dropzone__prompt--dragover');
    } else {
      this._prompt.classList.remove('dropzone__prompt--dragover');
    }
  }

  /**
   * @type {boolean}
   */
  get filled() {
    return this._prompt.classList.contains('dropzone__prompt--filled');
  }

  /**
   * @param {FileList} value
   */
  set file(value) {
    if (value == null) {
      const fileWording = this._allowMultiple ? 'Dateien' : 'Datei';

      this._el.files = null;
      this._el.value = null;
      this._firstLineElement.innerHTML = `${fileWording} hier hineinziehen, oder <button class="dropzone__upload" type="button">${fileWording} auswählen</button>`;
      this._secondLineElement.innerText = this.generateFileSpecification();

      this._iconElement.classList.remove('fa-file');
      this._iconElement.classList.add('fa-upload');

      this._prompt.classList.remove('dropzone__prompt--filled');

      return;
    }

    let fileNames = value[0].name;

    if (value.length > 1) {
      fileNames = `${value.length} Dateien`;
    }

    this._el.files = value;
    this._firstLineElement.innerText = fileNames;
    this._secondLineElement.innerText = `Upload erfolgt nach Absenden des Formulars`;

    this._iconElement.classList.remove('fa-upload')
    this._iconElement.classList.add('fa-file');

    this._prompt.classList.add('dropzone__prompt--filled');
  }

}

function init() {
  for (const dropzoneElement of document.getElementsByClassName('dropzone')) {
    tryExecution(() => new Dropzone(dropzoneElement));
  }
}

export default {
  init
}
